/**
 * ManageVPE State Hook
 * Manages all state for the ManageVPE component
 * Extracted from ManageVPE.tsx for better separation of concerns
 */

import { useState } from 'react';
import { VPE } from '@/services/features/VPEManagementService';

export interface ManageVPEState {
  // VPE Data
  vpes: VPE[];
  selectedVPE: VPE | null;

  // Loading States
  isLoading: boolean;
  isDeleteLoading: boolean;
  isCreateLoading: boolean;
  deleteButtonLoading: number | null;

  // Dialog States
  showDialog: boolean;
  showDeleteDialog: boolean;
  showLogoutDialog: boolean;

  // Form States
  isEditMode: boolean;
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  taskAssigned: boolean;
  showPassword: boolean;
  showConfirmPassword: boolean;

  // Pagination States
  currentPage: number;
  rowsPerPage: number;

  // Search States
  searchValue: string;
}

export interface ManageVPEStateActions {
  // VPE Data Actions
  setVpes: (vpes: VPE[]) => void;
  setSelectedVPE: (vpe: VPE | null) => void;

  // Loading Actions
  setIsLoading: (loading: boolean) => void;
  setIsDeleteLoading: (loading: boolean) => void;
  setIsCreateLoading: (loading: boolean) => void;
  setDeleteButtonLoading: (id: number | null) => void;

  // Dialog Actions
  setShowDialog: (show: boolean) => void;
  setShowDeleteDialog: (show: boolean) => void;
  setShowLogoutDialog: (show: boolean) => void;

  // Form Actions
  setIsEditMode: (editMode: boolean) => void;
  setFirstName: (name: string) => void;
  setLastName: (name: string) => void;
  setEmail: (email: string) => void;
  setPassword: (password: string) => void;
  setConfirmPassword: (password: string) => void;
  setTaskAssigned: (assigned: boolean) => void;
  setShowPassword: (show: boolean) => void;
  setShowConfirmPassword: (show: boolean) => void;

  // Pagination Actions
  setCurrentPage: (page: number) => void;
  setRowsPerPage: (rows: number) => void;

  // Search Actions
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  handleReload: () => void;

  // Utility Actions
  resetForm: () => void;
  populateFormForEdit: (vpe: VPE) => void;
}

export const useManageESState = () => {
  // VPE Data State
  const [vpes, setVpes] = useState<VPE[]>([]);
  const [selectedVPE, setSelectedVPE] = useState<VPE | null>(null);

  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [deleteButtonLoading, setDeleteButtonLoading] = useState<number | null>(null);

  // Dialog States
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Form States
  const [isEditMode, setIsEditMode] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [taskAssigned, setTaskAssigned] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search States
  const [searchValue, setSearchValue] = useState('');

  // Enhanced search handler that resets pagination
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    // Reset to first page when searching
    if (value !== searchValue) {
      setCurrentPage(1);
    }
  };

  // Handle search action
  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle reload action
  const handleReload = () => {
    setSearchValue('');
    setCurrentPage(1);
  };

  // Utility Functions
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setTaskAssigned(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsEditMode(false);
    setSelectedVPE(null);
  };

  const populateFormForEdit = (vpe: VPE) => {
    setSelectedVPE(vpe);
    setFirstName(vpe.first_name);
    setLastName(vpe.last_name);
    setEmail(vpe.email);
    setTaskAssigned(vpe.deferred_task);
    setPassword('');
    setConfirmPassword('');
    setIsEditMode(true);
  };

  // Search and Filter Logic
  const filteredVPEs = vpes.filter(vpe => {
    if (!searchValue) return true;

    const searchLower = searchValue.toLowerCase();
    return (
      vpe.es_name?.toLowerCase().includes(searchLower) ||
      vpe.email?.toLowerCase().includes(searchLower) ||
      vpe.first_name?.toLowerCase().includes(searchLower) ||
      vpe.last_name?.toLowerCase().includes(searchLower)
    );
  });

  // Computed Values
  const totalPages = Math.ceil(filteredVPEs.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedVPEs = filteredVPEs.slice(startIndex, startIndex + rowsPerPage);

  const state: ManageVPEState = {
    vpes,
    selectedVPE,
    isLoading,
    isDeleteLoading,
    isCreateLoading,
    deleteButtonLoading,
    showDialog,
    showDeleteDialog,
    showLogoutDialog,
    isEditMode,
    firstName,
    lastName,
    email,
    password,
    confirmPassword,
    taskAssigned,
    showPassword,
    showConfirmPassword,
    currentPage,
    rowsPerPage,
    searchValue,
  };

  const actions: ManageVPEStateActions = {
    setVpes,
    setSelectedVPE,
    setIsLoading,
    setIsDeleteLoading,
    setIsCreateLoading,
    setDeleteButtonLoading,
    setShowDialog,
    setShowDeleteDialog,
    setShowLogoutDialog,
    setIsEditMode,
    setFirstName,
    setLastName,
    setEmail,
    setPassword,
    setConfirmPassword,
    setTaskAssigned,
    setShowPassword,
    setShowConfirmPassword,
    setCurrentPage,
    setRowsPerPage,
    setSearchValue: handleSearchChange,
    handleSearch,
    handleReload,
    resetForm,
    populateFormForEdit,
  };

  return {
    state,
    actions,
    computed: {
      totalPages,
      startIndex,
      endIndex,
      paginatedVPEs,
      filteredVPEs,
      totalVPEs: filteredVPEs.length,
    },
  };
};